import { getUser } from "@utils/helpers";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface UserInfo {
  user: {
    id: string;
    username: string;
    avatar: string;
  };
  avatarSrc: string;
}

export enum AuthPlatform {
  DISCORD = "discord",
  GOOGLE = "google",
}

interface AuthContextValue {
  userInfo: UserInfo | null;
  isLoading: boolean;
  setUserInfo: (user: UserInfo | null) => void;
  authPlatform: AuthPlatform | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authPlatform, setAuthPlatform] = useState<AuthPlatform | null>(null);

  const removeTokenFromUrl = () => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("token");
    newUrl.searchParams.delete("platform");
    window.history.replaceState({}, "", newUrl.toString());
  };

  const removePurchaseParamsFromUrl = () => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("purchaseId");
    newUrl.searchParams.delete("checkoutId");
    newUrl.searchParams.delete("purchaseToken");
    window.history.replaceState({}, "", newUrl.toString());
  };

  const fetchUser = async () => {
    try {
      let jwt: string | null = null;

      // Get token from URL if available
      const urlParams = new URLSearchParams(window.location.search);
      jwt = urlParams.get("token");
      let _authPlatform = urlParams.get("platform") as AuthPlatform | null;
      // Move JWT from URL to sessionStorage and update URL
      if (jwt) {
        if (_authPlatform === AuthPlatform.DISCORD) {
          sessionStorage.setItem("pr_discord_jwt", jwt);
          setAuthPlatform(AuthPlatform.DISCORD);
        } else if (_authPlatform === AuthPlatform.GOOGLE) {
          sessionStorage.setItem("pr_google_jwt", jwt);
          setAuthPlatform(AuthPlatform.GOOGLE);
        }

        // Remove token from URL
        removeTokenFromUrl();
      } else {
        // If no token in URL, try getting it from sessionStorage
        jwt =
          sessionStorage.getItem("pr_discord_jwt") ||
          sessionStorage.getItem("pr_google_jwt");
      }

      // If no token is found, do not proceed with fetching
      if (!jwt) return;

      if (sessionStorage.getItem("pr_discord_jwt")) {
        setAuthPlatform(AuthPlatform.DISCORD);
        _authPlatform = AuthPlatform.DISCORD;
      } else if (sessionStorage.getItem("pr_google_jwt")) {
        setAuthPlatform(AuthPlatform.GOOGLE);
        _authPlatform = AuthPlatform.GOOGLE;
      }

      setIsLoading(true);
      const fetchedUserInfo = await getUser(jwt, _authPlatform);
      setUserInfo(fetchedUserInfo);
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("pr_discord_jwt");
    sessionStorage.removeItem("pr_google_jwt");
    setUserInfo(null);
  }

  useEffect(() => {
    fetchUser();
    removePurchaseParamsFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{ userInfo, setUserInfo, isLoading, authPlatform, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
